import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { appeler, ErreurApi, jetonActuel } from "../api/client";
import { useApi } from "../api/hooks";
import { EtatChargement, EtatErreur } from "../composants/Avis";
import { ApercuDocument } from "../composants/ApercuDocument";
import {
  ContenuDocumentaireSchema,
  ListeConfigSchema,
  type Document,
} from "../api/schemas";

/** Noeud de l'arbre documentaire. */
type Noeud = {
  nom: string;
  chemin: string;
  enfants: Map<string, Noeud>;
  documents: Document[];
};

function noeud(nom: string, chemin: string): Noeud {
  return { nom, chemin, enfants: new Map(), documents: [] };
}

/** Descend jusqu'au noeud d'un chemin, en creant ce qui manque au passage. */
function descendre(racine: Noeud, segments: readonly string[]): Noeud {
  let courant = racine;
  for (let i = 0; i < segments.length; i++) {
    const nom = segments[i];
    const chemin = segments.slice(0, i + 1).join("/");
    if (!courant.enfants.has(nom)) courant.enfants.set(nom, noeud(nom, chemin));
    courant = courant.enfants.get(nom)!;
  }
  return courant;
}

/**
 * ⛔ L'ARBRE SE CONSTRUIT DES DOSSIERS *ET* DES PIECES.
 *
 * Il ne se construisait que des pieces : un dossier vide n'apparaissait donc
 * jamais. Le client creait « Baux commerciaux », rechargeait la page, et son
 * dossier avait disparu — sans message, comme si le geste n'avait rien fait.
 */
function construireArbre(documents: readonly Document[], dossiers: readonly string[]): Noeud {
  const racine = noeud("", "");

  for (const d of dossiers) descendre(racine, d.split("/"));

  for (const d of documents) {
    const segments = d.cheminLogique.split("/");
    descendre(racine, segments.slice(0, -1)).documents.push(d);
  }
  return racine;
}

function Dossier({
  noeud: n,
  onApercu,
  onChoisir,
  cible,
  niveau = 0,
}: {
  noeud: Noeud;
  onApercu: (d: Document) => void;
  onChoisir: (chemin: string) => void;
  cible: string;
  niveau?: number;
}) {
  const [ouvert, setOuvert] = useState(niveau < 2);
  const choisi = cible === n.chemin;

  return (
    <li className="arbre__noeud">
      <span className={choisi ? "arbre__ligne arbre__ligne--choisie" : "arbre__ligne"}>
        <button type="button" className="arbre__dossier" onClick={() => setOuvert(!ouvert)}>
          <span aria-hidden>{ouvert ? "▾" : "▸"}</span> {n.nom}
          <span className="arbre__compte">
            {n.documents.length > 0 && ` (${n.documents.length})`}
          </span>
        </button>

        {/* Choisir la destination DANS l'arbre plutot que dans une liste
            deroulante : la liste comptait des centaines de lignes (5 types x 12
            mois x N exercices) et il fallait lire le chemin en entier pour
            savoir ou l'on deposait. */}
        <button type="button" className="arbre__choisir" onClick={() => onChoisir(n.chemin)}>
          {choisi ? "✓ destination" : "déposer ici"}
        </button>
      </span>

      {ouvert && (
        <ul className="arbre">
          {[...n.enfants.values()]
            .sort((a, b) => a.nom.localeCompare(b.nom, "fr"))
            .map((e) => (
              <Dossier
                key={e.chemin}
                noeud={e}
                onApercu={onApercu}
                onChoisir={onChoisir}
                cible={cible}
                niveau={niveau + 1}
              />
            ))}

          {n.documents.map((d) => (
            <li key={d.id} className="arbre__fichier">
              <button type="button" onClick={() => onApercu(d)}>
                {d.nomFichier}
              </button>
              <span className="arbre__taille">{Math.round(d.taille / 1024)} Ko</span>
              {d.nature && <span className="jeton">{d.nature}</span>}
              {d.scelle && <span className="jeton jeton--scelle">Scellé</span>}
              {d.altere && <span className="jeton jeton--alerte">Altéré</span>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * §5.A / §5.B — Onglet Documents du portail.
 *
 * Le classement a une forme commune a tous les clients : année / mois / TYPE.
 * Les types sont fixés par le cabinet ; le client crée ses sous-dossiers À
 * L'INTÉRIEUR, et dépose ses pièces en déclarant leur nature.
 */
export function DocumentsClient({ dossierId }: { dossierId: string }) {
  const qc = useQueryClient();
  const [apercu, setApercu] = useState<Document | null>(null);
  const [cible, setCible] = useState("");
  const [nature, setNature] = useState("");
  const [envoi, setEnvoi] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const q = useApi(
    ["documents-client", dossierId],
    `/dossiers/${dossierId}/documents`,
    ContenuDocumentaireSchema,
  );

  const natures = useApi(["natures-pieces"], "/configuration/natures-pieces", ListeConfigSchema);

  const arbre = useMemo(
    () => construireArbre(q.data?.documents ?? [], q.data?.dossiers ?? []),
    [q.data],
  );

  function rafraichir() {
    void qc.invalidateQueries({ queryKey: ["documents-client", dossierId] });
  }

  /**
   * Un dépôt passe par `fetch` et non par `appeler` : poser soi-même
   * `Content-Type` sur un `FormData` casserait la frontière multipart que le
   * navigateur génère lui-même.
   */
  async function envoyer(fichier: File, dossierCible: string) {
    const corps = new FormData();
    corps.append("fichier", fichier);
    corps.append("dossierCible", dossierCible);
    if (nature) corps.append("nature", nature);

    const jeton = jetonActuel();
    const r = await fetch(`/api/dossiers/${dossierId}/documents`, {
      method: "POST",
      body: corps,
      headers: jeton ? { Authorization: `Bearer ${jeton}` } : {},
    });

    if (!r.ok) {
      const detail = await r.json().catch(() => null);
      throw new ErreurApi(r.status, detail?.erreur ?? `Dépôt refusé (${r.status})`, detail?.motif);
    }
  }

  async function deposer(e: React.ChangeEvent<HTMLInputElement>) {
    const fichiers = e.target.files;
    if (!fichiers?.length) return;

    if (!cible) {
      setErreur("Choisissez d'abord un dossier de destination dans l'arbre.");
      e.target.value = "";
      return;
    }

    setErreur(null);
    try {
      for (const fichier of Array.from(fichiers)) {
        setEnvoi(fichier.name);

        // ⛔ IMPORT D'UN DOSSIER : on RECONSTRUIT l'arborescence d'origine.
        //
        // `webkitRelativePath` porte le chemin du fichier DANS le dossier
        // choisi — « Factures 2026/Janvier/f1.pdf ». L'ignorer aplatirait tout
        // dans un seul répertoire : cent fichiers en vrac, et le classement que
        // le client avait déjà fait sur son poste serait perdu à l'import.
        const relatif = (fichier as File & { webkitRelativePath?: string }).webkitRelativePath;
        const sousDossier = relatif ? relatif.split("/").slice(0, -1).join("/") : "";

        await envoyer(fichier, sousDossier ? `${cible}/${sousDossier}` : cible);
      }
      rafraichir();
    } catch (err) {
      setErreur(err instanceof ErreurApi ? (err.motif ?? err.message) : "Dépôt impossible.");
    } finally {
      setEnvoi(null);
      e.target.value = "";
    }
  }

  async function creerDossier() {
    if (!cible) {
      setErreur("Choisissez d'abord le dossier parent dans l'arbre.");
      return;
    }

    const nom = window.prompt("Nom du nouveau dossier");
    if (!nom?.trim()) return;

    setErreur(null);
    try {
      await appeler(
        `/dossiers/${dossierId}/documents/dossier`,
        z.object({ chemin: z.string(), deja: z.boolean() }),
        { method: "POST", body: JSON.stringify({ chemin: `${cible}/${nom.trim()}` }) },
      );
      rafraichir();
    } catch (err) {
      setErreur(err instanceof ErreurApi ? (err.motif ?? err.message) : "Création impossible.");
    }
  }

  if (q.isLoading) return <EtatChargement quoi="de vos documents" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  return (
    <div>
      <section className="depot">
        <h3>Déposer des pièces</h3>

        <p className="resume">
          Destination : <strong>{cible || "aucune — choisissez un dossier ci-dessous"}</strong>
        </p>

        <label>
          Nature de la pièce
          <select value={nature} onChange={(e) => setNature(e.target.value)}>
            <option value="">Non précisée</option>
            {(natures.data ?? [])
              .filter((n) => n.actif)
              .map((n) => (
                <option key={n.id} value={n.cle ?? ""}>{n.libelle}</option>
              ))}
          </select>
        </label>

        <div className="depot__actions">
          <label className="bouton-creux">
            Fichiers
            <input type="file" multiple onChange={deposer} disabled={!!envoi || !cible} hidden />
          </label>

          {/* `webkitdirectory` n'est pas dans les types React : il est pourtant
              gere par tous les navigateurs cibles, et c'est la seule facon
              d'importer une arborescence entiere. */}
          <label className="bouton-creux">
            Un dossier entier
            <input
              type="file"
              multiple
              onChange={deposer}
              disabled={!!envoi || !cible}
              hidden
              {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            />
          </label>

          <button type="button" className="bouton-creux" onClick={() => void creerDossier()}>
            + Nouveau dossier
          </button>
        </div>

        {envoi && <p>Dépôt de {envoi}…</p>}
        {erreur && <p className="alerte" role="alert">{erreur}</p>}

        <p className="note">
          Le type de chaque fichier est vérifié sur son contenu réel, pas sur son
          extension. Les types de dossiers — juridique, fiscal, social… — sont
          fixés par le cabinet ; vos sous-dossiers se créent à l'intérieur.
        </p>
      </section>

      <section>
        <h3>Vos dossiers</h3>
        <ul className="arbre arbre--racine">
          {[...arbre.enfants.values()]
            .sort((a, b) => b.nom.localeCompare(a.nom, "fr"))
            .map((e) => (
              <Dossier
                key={e.chemin}
                noeud={e}
                onApercu={setApercu}
                onChoisir={setCible}
                cible={cible}
              />
            ))}
        </ul>
      </section>

      {apercu && (
        <ApercuDocument dossierId={dossierId} document={apercu} onFermer={() => setApercu(null)} />
      )}
    </div>
  );
}

export { z };
